// This function is being used
export function apiResponseMiddleware(data: any) {
  const uiConfig: any = data?.data?.ruleData?.uiElements?.uiConfig;
  const graphQLResponse: any = data?.data?.ruleData?.graphQLResponse;

  if (!uiConfig || typeof uiConfig !== "object") {
    console.log("Invalid uiConfig");
    return {};
    // throw new Error("Invalid uiConfig");
  }

  return {
    alerts: uiConfig?.alerts,
    buttons: uiConfig?.buttons,
    sections: uiConfig?.sections,
    dropdowns: uiConfig?.dropdowns,
    dynamicSections: uiConfig?.dynamicSections,
    documents: uiConfig?.documentSection,
    items: uiConfig?.items,
    graphQLResponse
  };
}

const keysForSections = ["sections", "dynamicSections", "items"]; // Keys containing data for sections in response of UI Config

/**
 *
 * @date 17-Nov-2024
 * @param element element parameter is for passing the element it can be button, field or whole layout
 * @param elementResponse element response parameter will check whether it is visible or not
 * @returns it will be return true or false on the basis of isVisible key
 */
export const checkVisibility = (element: any) =>
  element?.visible?.isVisible || false;

export const checkButtonVisibility = (button: any) =>
  button?.isVisible || false;

export const checkIfAnyButtonVisible = (buttons: any) => {
  return (
    buttons &&
    Object.keys(buttons)?.some((b: any) => buttons[b]?.visible?.isVisible)
  );
};

export const checkNestedFieldVisibilityButIncludeID = (field: any) =>
  field?.visible?.isVisible || field?.id || false;

export const isFieldRequired = (field: any) =>
  field?.mandatory?.isMandatory || false;

export const getValueOfKeyFromConfig = (fieldList: any, key: string) =>
  fieldList?.find((field: any) => field.id === key)?.value;

export const checkSectionEditability = (section: any) => {
  const isVisibleEditBtn = section?.buttonList?.edit?.visible?.isVisible,
    sectionTitle = section?.buttonList?.edit?.hoverString;

  return isVisibleEditBtn && sectionTitle === `Edit ${section?.label}`;
};

export const checkEditability = (field: any) =>
  field?.editable?.isEditable || false;

export const checkNestedSectionEditability = (section: any) =>
  section?.buttonList?.editReference?.visible?.isVisible || false;

export const checkNestedSectionDeletability = (section: any) =>
  section?.buttonList?.removeReference?.visible?.isVisible || false;

export const checkCoApplicantDeletablity = (section: any) =>
  section?.buttonList?.deleteCoApplicant?.visible?.isVisible || false;

export const checkCoApplicantResumeActionVisiblity = (section: any) =>
  section?.nestedfieldList?.find(
    (item: any) => item.id == "status" && item.value !== 4
  );

export const getVisibleButtonNames = (buttons: any) =>
  buttons &&
  Object.keys(buttons)?.filter((b: any) => buttons[b]?.visible?.isVisible);

export const sortButtonsByPosition = (buttonsArray: [string, any][]) => {
  return buttonsArray.sort(([_, a], [__, b]) => {
    const posA = typeof a?.position === "number" ? a?.position : Infinity;
    const posB = typeof b?.position === "number" ? b?.position : Infinity;

    return posA - posB;
  });
};

export const getPanelWidth = (size?: string) => {
  switch (size) {
    case "l":
      return "!w-[900px]";
    case "m":
      return "!w-[680px]";
    case "s":
    default:
      return "!w-[480px]";
  }
};

// Dynamically constructs the clientSideInput based on the button's action body
export const getDynamicClientSideInput = (btn: any, item: any) => {
  const dynamicInput: Record<string, string> = {};
  const actionBody = btn?.action?.params?.body;

  if (!actionBody) return dynamicInput;

  // Scan through action payload body to resolve any '${clientSideInput.some_key}' references
  Object.entries(actionBody).forEach(([key, val]) => {
    if (typeof val === "string") {
      const match = val.match(/^\$\{clientSideInput\.(.+)\}$/);
      if (match) {
        const fieldKey = match[1];

        // Fetch the corresponding value from nestedfieldList and assign it to the fieldKey
        dynamicInput[fieldKey] =
          getValueOfKeyFromConfig(item?.nestedfieldList, fieldKey) || "";
      } else {
        // Plain string — assign under its own key as-is
        dynamicInput[key] = val;
      }
    } else {
      // Non-string value (object, number, boolean, etc.) — assign as-is
      dynamicInput[key] = val;
    }
  });

  return dynamicInput;
};

export const getVisibleAndEditableFieldsConfig = (
  data: any,
  includeIDDynamicSection = true
) => {
  // For testing purposes, make data = {buttons: {}, sections: {}, dynamicSections: {}}

  const sectionsData = keysForSections.reduce(
    (acc: any, key: string) => ({
      ...acc,
      ...(data[key] ?? {})
    }),
    {}
  );

  let filteredVisibleSections = Object.entries(sectionsData)
    .filter(([_, section]: any) => checkVisibility(section))
    .map(([key, section]: any) => {
      // Filter visible buttons from buttonList
      const visibleButtons = section?.buttonList
        ? Object.fromEntries(
            Object.entries(section.buttonList).filter(([_, button]: any) =>
              checkVisibility(button)
            )
          )
        : undefined;

      return {
        ...section,
        fieldList: section.fieldList?.filter(checkVisibility),
        ...(visibleButtons &&
          Object.keys(visibleButtons).length > 0 && {
            buttonList: visibleButtons
          })
      };
    });

  // For checking the nested fields list visibility in the case of dynamicSections
  if (
    filteredVisibleSections?.some((section: any) =>
      section?.fieldList?.some((field: any) => field?.nestedfieldList)
    )
  ) {
    filteredVisibleSections = filteredVisibleSections.map((section: any) => {
      return {
        ...section,
        fieldList: section.fieldList?.map((field: any) => ({
          ...field,
          nestedfieldList: field?.nestedfieldList?.filter(
            includeIDDynamicSection
              ? checkNestedFieldVisibilityButIncludeID
              : checkVisibility
          )
        }))
      };
    });
  }

  // Filtering editable fields out of the visible fields for each section
  let filteredEditableFields = Object.entries(filteredVisibleSections).map(
    ([key, section]: any) => {
      return {
        ...section,
        fieldList: section.fieldList?.filter(checkEditability)
      };
    }
  );

  // For checking the nested fields list editability in the case of dynamicSections
  if (
    filteredEditableFields?.some((section: any) =>
      section?.fieldList?.some((field: any) => field?.nestedfieldList)
    )
  ) {
    filteredEditableFields = filteredEditableFields?.map((section: any) => {
      return {
        ...section,
        fieldList: section.fieldList?.map((field: any) => {
          return {
            ...field,
            nestedfieldList: field?.nestedfieldList?.filter(checkEditability)
          };
        })
      };
    });
  }

  const visibleButtons = Object.fromEntries(
    Object.entries(data?.buttons ?? {}).filter(
      ([_, config]: any) => config?.visible?.isVisible
    )
  );

  return {
    ...data,
    buttons: visibleButtons,
    sections: filteredVisibleSections,
    editableFieldsSectionWise: filteredEditableFields
  };
};

const formatFieldValue = (value: any, id: string): string => {
  if (value === null || value === undefined || typeof value === "object")
    return "";

  if (typeof value === "number") {
    if (value <= 1) return `${(value * 100).toFixed(1)}%`;

    if (id !== "margin" && id !== "baseInterest") {
      return value.toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0
      });
    }

    return value?.toString();
  }
  return value.toString();
};

export const getVisibleFieldsForLoanOffer = (data: any) => {
  // Define which fields belong to which section
  const loanTypeFields = [
      "program",
      "product",
      "scheme",
      "loanPurpose",
      "propertyType",
      "subProduct"
    ],
    loanDetailsFields = [
      "roi",
      "requestedROI",
      "foir",
      "ltv",
      "tenure",
      "trancheType",
      "requestedLoanAmount",
      "processingFee",
      "emi",
      "typeOfInterest",
      "typeOfInterestTiered",
      "baseInterest",
      "margin",
      "processingFee",
      "processingFeeinRupees",
      "incomeProgramme"
    ];

  const visibleFields =
    data?.loanOfferSectionData?.fieldList ??
    [].filter((field: any) => field?.visible?.isVisible);

  return [
    {
      title: "Loan Type",
      key: "loan_type",
      id: "loan_type",
      isEditable: false,
      fieldList: visibleFields
        .filter((field: any) => loanTypeFields.includes(field.id))
        .map((field: any) => ({
          name: field?.label,
          value: formatFieldValue(field?.value, field?.id) || "-",
          key: field?.id,
          id: field?.id
        }))
    },
    {
      title: "Loan Details",
      key: "loan_details",
      id: "loan_details",
      isEditable: false,
      fieldList: visibleFields
        .filter((field: any) => loanDetailsFields.includes(field.id))
        .map((field: any) => ({
          name: field?.label,
          value: formatFieldValue(field?.value, field?.id) || "-",
          key: field?.id,
          id: field?.id
        }))
    }
  ];
};
